import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Users,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Eye,
  EyeOff,
  Lock,
  Mail
} from "lucide-react";

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth, hydrateAfterLogin } = useAuth();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Password-signup state (new: lets invited employees create an account
  // without needing Google or checking email for a magic link).
  const [pwdEmail, setPwdEmail] = useState("");
  const [pwdPassword, setPwdPassword] = useState("");
  const [pwdName, setPwdName] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    fetchInviteInfo();
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const response = await axios.get(`${API}/team/invite/${token}/info`);
      setInviteInfo(response.data);
      if (response.data?.email) {
        setPwdEmail(response.data.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired invite link");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Signed-out users use the inline password / Google / magic-link forms
      // below — no need to redirect.
      return;
    }

    setAccepting(true);
    try {
      const response = await apiClient.post(`/team/invite/${token}/accept`);
      setAccepted(true);
      
      // Refresh auth to get new tenant info
      await checkAuth();
      
      // Redirect to dashboard after a moment
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to accept invite");
    } finally {
      setAccepting(false);
    }
  };

  const handleLogin = () => {
    // Route through OUR Google OAuth start so we can bounce back cleanly to
    // this invite page. State encodes where the callback should return.
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const state = encodeURIComponent(`${window.location.origin}/invite/${token}`);
    window.location.href = `${apiBase}/api/auth/google/start?state=${state}`;
  };

  const handleInviteSignup = async (e) => {
    e?.preventDefault?.();
    const email = pwdEmail.trim().toLowerCase();
    if (!email || pwdPassword.length < 8) {
      toast.error("Enter your email and a password of at least 8 characters.");
      return;
    }
    setSignupSubmitting(true);
    try {
      // Sign up WITHOUT company_name — the invite acceptance step below
      // will link this new user to the inviting tenant.
      const res = await axios.post(`${API}/auth/signup`, {
        email,
        password: pwdPassword,
        name: pwdName.trim() || undefined,
      });
      localStorage.setItem("session_token", res.data.session_token);
      // Now immediately accept the invite so we skip the "signed in but not
      // in a team yet" purgatory state.
      const acc = await axios.post(`${API}/team/invite/${token}/accept`, null, {
        headers: { Authorization: `Bearer ${res.data.session_token}` },
      });
      // Load /auth/me + tenant into context, then jump to dashboard.
      const me = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${res.data.session_token}` },
      });
      if (hydrateAfterLogin) await hydrateAfterLogin(me.data);
      setAccepted(true);
      setTimeout(() => { window.location.href = "/dashboard"; }, 900);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : "Could not create account. Please try again.";
      toast.error(msg);
    } finally {
      setSignupSubmitting(false);
    }
  };

  const handleInviteMagicLink = async () => {
    const email = pwdEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Enter your email first.");
      return;
    }
    setMagicSending(true);
    try {
      await axios.post(`${API}/auth/magic-link/request`, { email });
      setMagicSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send sign-in email.");
    } finally {
      setMagicSending(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "owner": return "bg-[#0A192F] hover:bg-[#0A192F]";
      case "manager": return "bg-[#0D9488] hover:bg-[#0D9488]";
      default: return "bg-slate-500 hover:bg-slate-500";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0D9488] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error && !accepted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border border-slate-200">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-[#E11D48] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0A192F] mb-2">Invalid Invite</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border border-slate-200">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0A192F] mb-2">Welcome to the Team!</h2>
            <p className="text-slate-600 mb-2">
              You've successfully joined <strong>{inviteInfo?.company_name}</strong>
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Redirecting to dashboard...
            </p>
            <div className="w-8 h-8 border-4 border-[#0D9488] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border border-slate-200" data-testid="accept-invite-card">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#0D9488] rounded-full flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#0A192F]">
            You're Invited!
          </CardTitle>
          <CardDescription>
            Join {inviteInfo?.company_name} on Nirvanta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Building2 className="w-4 h-4" />
                <span>Company</span>
              </div>
              <span className="font-semibold text-[#0A192F]">{inviteInfo?.company_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Users className="w-4 h-4" />
                <span>Your Role</span>
              </div>
              <Badge className={getRoleBadgeColor(inviteInfo?.role)}>
                {inviteInfo?.role}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Users className="w-4 h-4" />
                <span>Invited By</span>
              </div>
              <span className="text-[#0A192F]">{inviteInfo?.invited_by}</span>
            </div>
          </div>

          <div className="text-sm text-slate-500 text-center flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            <span>This invite expires on {inviteInfo?.expires_at ? new Date(inviteInfo.expires_at.split('T')[0] + 'T12:00:00').toLocaleDateString() : ''}</span>
          </div>

          {user ? (
            <div className="space-y-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">
                  Signed in as <strong>{user.email}</strong>
                </p>
              </div>
              {user.tenant_id ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-[#E11D48]">
                    You're already part of another company. Leave your current company first to accept this invite.
                  </p>
                </div>
              ) : (
                <Button 
                  className="w-full bg-[#0D9488] hover:bg-[#14B8A6]"
                  onClick={handleAccept}
                  disabled={accepting}
                  data-testid="accept-invite-btn"
                >
                  {accepting ? "Joining..." : "Accept & Join Team"}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {magicSent ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center space-y-2" data-testid="invite-magic-sent">
                  <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto" />
                  <p className="text-sm text-slate-700">
                    We sent a sign-in link to <strong>{pwdEmail}</strong>. Open it to join the team.
                  </p>
                  <button
                    type="button"
                    onClick={() => setMagicSent(false)}
                    className="text-xs text-slate-500 hover:text-slate-700 underline"
                  >
                    Use a different method
                  </button>
                </div>
              ) : (
                <>
                  <form onSubmit={handleInviteSignup} className="space-y-3" data-testid="invite-signup-form">
                    <p className="text-sm font-semibold text-slate-700">Create your account to join</p>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-slate-600">
                        Email {inviteInfo?.email && <span className="text-slate-400">(from your invite)</span>}
                      </label>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={pwdEmail}
                        onChange={(e) => setPwdEmail(e.target.value)}
                        disabled={signupSubmitting || Boolean(inviteInfo?.email)}
                        readOnly={Boolean(inviteInfo?.email)}
                        data-testid="invite-email-input"
                        autoComplete="email"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center justify-between text-xs font-medium text-slate-600">
                        <span>Password (min 8 characters)</span>
                        <button
                          type="button"
                          onClick={() => setShowPwd((v) => !v)}
                          className="text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
                          data-testid="invite-show-password"
                        >
                          {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          {showPwd ? "Hide" : "Show"}
                        </button>
                      </label>
                      <Input
                        type={showPwd ? "text" : "password"}
                        placeholder="Choose a password"
                        value={pwdPassword}
                        onChange={(e) => setPwdPassword(e.target.value)}
                        disabled={signupSubmitting}
                        data-testid="invite-password-input"
                        autoComplete="new-password"
                        minLength={8}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-slate-600">Your name <span className="text-slate-400">(optional)</span></label>
                      <Input
                        type="text"
                        placeholder="First Last"
                        value={pwdName}
                        onChange={(e) => setPwdName(e.target.value)}
                        disabled={signupSubmitting}
                        data-testid="invite-name-input"
                        autoComplete="name"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#0D9488] hover:bg-[#14B8A6]"
                      disabled={signupSubmitting || !pwdEmail || !pwdPassword}
                      data-testid="invite-signup-submit-btn"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      {signupSubmitting ? "Creating account…" : "Create account & join team"}
                    </Button>
                  </form>

                  <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
                    <span className="h-px bg-slate-200 flex-1" />
                    <span>or</span>
                    <span className="h-px bg-slate-200 flex-1" />
                  </div>

                  <Button
                    className="w-full bg-[#0A192F] hover:bg-[#112240]"
                    onClick={handleLogin}
                    data-testid="login-to-accept-btn"
                  >
                    Continue with Google
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleInviteMagicLink}
                    disabled={magicSending || !pwdEmail}
                    data-testid="invite-magic-link-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {magicSending ? "Sending…" : "Email me a sign-in link"}
                  </Button>
                </>
              )}
            </div>
          )}

          <div className="text-center">
            <Button 
              variant="link" 
              onClick={() => navigate('/')}
              className="text-slate-500"
            >
              Go to Homepage
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvite;
